// Поле с подсказками (города, станции, отели). Выбранное значение — в атрибуте data-value поля ввода.
// Совместимость с внешними скриптами: ввод слушаем нативным событием 'input' (el.value = 'x' + dispatchEvent
// тоже запускает поиск), подсказку выбираем по mousedown, клавиши ↑ ↓ Enter Tab Esc.
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { api } from '../lib/api';
import { Icon, type IconName } from './Icon';

export interface Picked {
  label: string;
  /** код выбранного варианта; '' — пользователь ввёл текст, но ничего не выбрал */
  value: string;
}

export interface AutocompleteItem extends Picked {
  node: ReactNode;
}

export interface AutocompleteProps<T> {
  id?: string;
  label: string;
  icon: IconName;
  placeholder?: string;
  endpoint: string;
  mapItem: (item: T) => AutocompleteItem;
  value: Picked | null;
  onChange: (v: Picked | null) => void;
  /** выбран вариант из подсказок */
  onPick?: (item: AutocompleteItem) => void;
  className?: string;
  invalid?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}

export function Autocomplete<T>(props: AutocompleteProps<T>) {
  const { id, label, icon, placeholder, value, className, invalid, inputRef } = props;
  const input = useRef<HTMLInputElement | null>(null);
  const latest = useRef(props);
  latest.current = props;
  const [items, setItems] = useState<AutocompleteItem[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const st = useRef({ timer: 0 as ReturnType<typeof setTimeout> | 0, seq: 0, items: [] as AutocompleteItem[], open: false, active: -1 });
  st.current.items = items;
  st.current.open = open;
  st.current.active = active;

  // Значение, заданное кодом (выбор, обмен местами, восстановление из ссылки), — в поле ввода
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    const text = value ? value.label : '';
    if (el.value !== text) el.value = text;
    if (value && value.value) el.dataset.value = value.value; else delete el.dataset.value;
  }, [value]);

  useEffect(() => {
    const el = input.current;
    if (!el) return;
    const hide = () => setOpen(false);
    const onInput = () => {
      delete el.dataset.value;
      el.classList.remove('invalid');
      latest.current.onChange(el.value ? { label: el.value, value: '' } : null);
      const s = st.current;
      if (s.timer) clearTimeout(s.timer);
      s.timer = setTimeout(async () => {
        const term = el.value.trim();
        if (term.length < 2) { hide(); return; }
        const mySeq = ++s.seq;
        const data = await api<T[]>(`${latest.current.endpoint}?term=${encodeURIComponent(term)}`).catch(() => [] as T[]);
        if (mySeq !== s.seq || document.activeElement !== el) return;
        const list = Array.isArray(data) ? data.map(latest.current.mapItem) : [];
        setItems(list);
        setActive(-1);
        setOpen(list.length > 0);
      }, 200);
    };
    const onBlur = () => { setTimeout(hide, 100); };
    el.addEventListener('input', onInput);
    el.addEventListener('blur', onBlur);
    return () => {
      el.removeEventListener('input', onInput);
      el.removeEventListener('blur', onBlur);
      if (st.current.timer) clearTimeout(st.current.timer);
    };
  }, []);

  const pick = (item: AutocompleteItem | undefined) => {
    if (!item) return;
    const el = input.current;
    if (el) {
      el.value = item.label;
      el.dataset.value = item.value;
      el.classList.remove('invalid');
    }
    setOpen(false);
    latest.current.onChange({ label: item.label, value: item.value });
    latest.current.onPick?.(item);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const s = st.current;
    if (!s.open || !s.items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((s.active + (e.key === 'ArrowDown' ? 1 : -1) + s.items.length) % s.items.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (e.key === 'Enter') e.preventDefault();
      pick(s.items[Math.max(s.active, 0)]);
    } else if (e.key === 'Escape') setOpen(false);
  };

  const setRefs = (el: HTMLInputElement | null) => {
    input.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = el;
  };

  return (
    <div className={className ? `field place ${className}` : 'field place'}>
      <label htmlFor={id}>{label}</label>
      <div className="control">
        <Icon name={icon} />
        <input id={id} ref={setRefs} placeholder={placeholder} className={invalid ? 'invalid' : undefined} onKeyDown={onKeyDown} autoComplete="off" />
      </div>
      <ul className="suggest" hidden={!open}>
        {items.map((it, i) => (
          <li
            key={`${it.value}-${i}`}
            data-i={i}
            className={i === active ? 'active' : undefined}
            onMouseDown={(e) => { e.preventDefault(); pick(it); }}
          >
            {it.node}
          </li>
        ))}
      </ul>
    </div>
  );
}
